// Imported from their own modules rather than the shared barrel: the barrel
// exports components first, and those pull this file back in, so under the
// karma bundle's module order the enums were still undefined when this ran
// ("Cannot read properties of undefined (reading 'CurrencyFormats')").
import { BudgetTypes } from '@macs-frontend/web-macs/shared/enums/team-task-budgets.enum';
import { CurrencyFormats } from '@macs-frontend/web-macs/shared/enums/currency-formats.enum';
import { Days } from '@macs-frontend/web-macs/shared/enums/days.enum';
import { Months } from '@macs-frontend/web-macs/shared/enums/months.enum';
import { NumberFormats } from '@macs-frontend/web-macs/shared/enums/number-formats.enum';
import { Roundings } from '@macs-frontend/web-macs/shared/enums/roundings.enum';
import { Currency } from '@macs-frontend/web-macs/shared/models/currency.model';
import { isoUUID } from './uuid';

export const defaultDateFormat = 'MM/dd/yyyy';
export const internalDateFormat = 'yyyy-MM-dd';
export const defaultNumberFormat = '1 234,56';
export const defaultTimezoneOffset = 0;
export const defaultCurrency: Currency = {
  sign: '€',
  name: 'Euro',
  code: 'EUR',
  uuid: isoUUID.wrap('3e1d4008-f21f-4c7f-9b5b-8f9f447af06a'),
};

export enum ListsUrls {
  Currency = 'control_center/currencies',
  Services = 'projects/dictionaries/services',
  Transport = 'projects/dictionaries/transports',
  Clients = 'company_management/manage/clients?lite=1',
  // Materials create/edit form: archived clients must not be offered (same
  // rationale as ViaCompanies below). Scoped to the materials form so the
  // shared `Clients` dropdown elsewhere keeps showing archived clients.
  MaterialClients = 'company_management/manage/clients?lite=1&status=ACTIVE',
  ProjectGroups = 'projects/project_groups',
  Tasks = 'company_management/manage/tasks?status=ACTIVE',
  TaskGroups = 'company_management/manage/taskGroups?status=ACTIVE',
  TaskExpenseSingleItems = 'company_management/manage/task-expense/single-items',
  TaskExpenseGroups = 'company_management/manage/task-expense/groups',
  Materials = 'projects/dictionaries/materials',
  SampleUnits = 'projects/dictionaries/sample_units',
  Users = 'company_management/manage/users?_shape=@users&status=ACTIVE',
  Projects = 'projects/projects',
  ProjectTeamMembers = 'projects/project_team_members',
  ClientContacts = 'company_management/manage/contact_clients',
  Cities = 'geo/cities',
  Countries = 'countries',
  Categories = 'company_management/manage/company_categories',
  Suppliers = 'company_management/manage/suppliers',
  ExternalUsers = 'hr/external/users',
  ContractTemplates = 'hr/administration/contract_templates',
  TimeTracingProjectsTasks = 'time_tracking/time_tracks/projects_tasks',
  Contacts = 'company_management/manage/contacts',
  VatInfo = 'company_management/manage/vat',
  Expenses = 'company_management/manage/expenses?status=ACTIVE',
  Fees = 'company_management/manage/fees',
  FeeGroups = 'company_management/manage/feeGroups',
  Invoices = 'company_management/financial/invoices',
  InvoiceItems = 'company_management/financial/invoice-items',
  InvoiceGroups = 'company_management/financial/invoice-groups',
  OccupationTypes = 'company_management/manage/occupation_types',
  ExpenseGroups = 'company_management/manage/expense_groups?status=ACTIVE',
  Roles = 'control_center/roles',
  ExternalRoles = 'projects/action_team_roles',
  Ratesheets = 'company_management/manage/ratesheets?status=ACTIVE',
  Warehouses = 'projects/dictionaries/warehouses',
  // Materials use Team Cost "Via companies" as their warehouse (storage
  // location) reference — this is what ms-materials replicates into
  // ref_warehouse, so names resolve. NOT the pricing dictionary above.
  // ACTIVE only (same as Ratesheets/ExpenseGroups): archived via-companies
  // must not be offered as storage locations for new/edited materials.
  ViaCompanies = 'team-cost-via-companies?status=ACTIVE',
  BookingPersons = 'company_management/planner/booking-persons',
  CPO = 'projects/cpo',
  AssignmentTimeslots = 'projects/timeslots',
  Notifications = 'notifications',
  AllTasks = 'company_management/manage/tasks',
  TaskRoles = 'company_management/manage/task_roles',
  Nationalities = 'nationalities',
  Tags = 'tags',
  GeoCities = 'geo/cities',
  GeoCountries = 'geo/countries',
  Knowledge = 'hr/knowledge',
  Brands = 'projects/brands',
  // TCTeamTypes = 'team-cost-team-types',
  TeamCostRoles = 'team-cost-roles',
  TeamCostLevels = 'team-cost-levels',
  TeamCostJointCommittees = 'team-cost-joint-committees',
  TeamCostTeamTypes = 'team-cost-team-types',
  TaxRates = 'company_management/manage/tax-rates',
  TeamCostScheduleAssigmentTags = 'team-costs/schedule-assignment-tags',
}

export const CompanySettingsConsts: {
  currencyFormats: { name: string; id: CurrencyFormats }[];
  numberFormats: NumberFormats[];
  months: Months[];
  days: Days[];
  roundings: { name: string; id: Roundings }[];
  dateFormats: string[];
} = {
  currencyFormats: [
    { name: 'Symbol before', id: CurrencyFormats.SYMBOL_BEFORE },
    { name: 'Symbol after', id: CurrencyFormats.SYMBOL_AFTER },
    { name: 'ISO code after', id: CurrencyFormats.ISO_CODE_AFTER },
    { name: 'ISO code before', id: CurrencyFormats.ISO_CODE_BEFORE },
    {
      name: 'Symbol before ISO code after',
      id: CurrencyFormats.SYMBOL_BEFORE_ISO_CODE_AFTER,
    },
    {
      name: 'ISO code before symbol before',
      id: CurrencyFormats.ISO_CODE_BEFORE_SYMBOL_BEFORE,
    },
    {
      name: 'Symbol after ISO code after',
      id: CurrencyFormats.SYMBOL_AFTER_ISO_CODE_AFTER,
    },
    {
      name: 'ISO code before symbol after',
      id: CurrencyFormats.ISO_CODE_BEFORE_SYMBOL_AFTER,
    },
  ],
  numberFormats: [
    NumberFormats.NUMBER_FORMAT_COMMA,
    NumberFormats.NUMBER_FORMAT_POINT_COMMA,
    NumberFormats.NUMBER_FORMAT_APOSTROPHE_POINT,
    NumberFormats.NUMBER_FORMAT_SPACE_COMMA,
  ],
  months: [
    Months.January,
    Months.February,
    Months.March,
    Months.April,
    Months.May,
    Months.June,
    Months.July,
    Months.August,
    Months.September,
    Months.October,
    Months.November,
    Months.December,
  ],
  days: [Days.Monday, Days.Saturday, Days.Sunday],
  roundings: [
    { name: 'No rounding', id: Roundings.NO_ROUNDING },
    { name: 'Round to nearest', id: Roundings.ROUND_TO_NEAREST },
    { name: 'Round up to nearest', id: Roundings.ROUND_UP_TO_NEAREST },
  ],
  dateFormats: [
    'dd.MM.yyyy',
    'yyyy.MM.dd',
    defaultDateFormat,
    'dd/MM/yyyy',
    'yyyy-MM-dd',
    'yyyy/MM/dd',
  ],
};

export const defaultItemsPerPage = 20;

export const hoursBudgetTypes = [
  BudgetTypes.HOURS_PER_PERSON,
  BudgetTypes.HOURS_PER_TASK,
  BudgetTypes.TOTAL_PROJECT_HOURS,
  BudgetTypes.TOTAL_BUDGET_AND_HOURS_PER_TASK,
];

export enum TimeFormats {
  HOURS_24 = '24_HOURS',
  HOURS_12 = '12_HOURS',
}

export enum TimeDisplays {
  DECIMAL = 'Decimal',
  HH_MM = 'HH:mm',
}

export enum FetchPolicy {
  CacheFirst = 'cache-first',
  NetworkOnly = 'network-only',
  CacheOnly = 'cache-only',
  NoCache = 'no-cache',
  StandBy = 'standby',
}

export enum WatchQueryFetchPolicy {
  CacheFirst = 'cache-first',
  NetworkOnly = 'network-only',
  CacheOnly = 'cache-only',
  NoCache = 'no-cache',
  StandBy = 'standby',
  CacheAndNetwork = 'cache-and-network',
}

export enum Loaders {
  PlannerProjectsRoleSlot = 'PlannerProjectsRoleSlot',
  PlannerProjectsTransportSlot = 'PlannerProjectsTransportSlot',
}
